// FWAA Business Exchange — My Appointments.
// The rep's live working list: every claimed attendee with a notes
// thread and a Mark Completed toggle. Completed appointments stay on
// the list (Matt's call) — this doubles as the follow-up log after
// the event.

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { ClaimSummary, CompleteToggle, NotesThread, RoleBadge } from '../components/FwaaShared';

export default function FwaaMyAppointmentsPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    api.getFwaaMyAppointments()
      .then(d => { setData(d); setError(''); })
      .catch(err => setError(err?.responseBody?.error || err.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const appts = data?.appointments || [];
  const active = appts.filter(a => a.status !== 'completed');
  const done = appts.filter(a => a.status === 'completed');

  return (
    <div className="directory-page fwaa-page">
      <Link to="/sales/fwaa" className="fwaa-back">← FWAA Business Exchange</Link>

      <div className="fwaa-hero">
        <div>
          <h1 className="fwaa-hero__title">My Appointments</h1>
          <p className="fwaa-hero__sub">
            {loading ? 'Loading...' : `${active.length} up next · ${done.length} completed`}
          </p>
        </div>
        <ClaimSummary board={data?.claim_board} />
      </div>

      {error ? <div className="card" style={{ padding: 16, color: '#dc2626' }}>{error}</div> : null}

      {!loading && appts.length === 0 ? (
        <div className="fwaa-claimboard-empty">
          You haven&rsquo;t claimed any appointments yet. Head to the{' '}
          <Link to="/sales/fwaa">board</Link> and grab your targets.
        </div>
      ) : null}

      {active.length > 0 ? (
        <>
          <h2 className="fwaa-section-title">Up next ({active.length})</h2>
          <div className="fwaa-appt-list">
            {active.map(a => <ApptCard key={a.id} appt={a} myUserId={user?.id} onChanged={load} />)}
          </div>
        </>
      ) : null}

      {done.length > 0 ? (
        <>
          <h2 className="fwaa-section-title">Completed ({done.length})</h2>
          <div className="fwaa-appt-list">
            {done.map(a => <ApptCard key={a.id} appt={a} myUserId={user?.id} onChanged={load} />)}
          </div>
        </>
      ) : null}
    </div>
  );
}

function ApptCard({ appt, myUserId, onChanged }) {
  const completed = appt.status === 'completed';
  return (
    <div className={`fwaa-appt${completed ? ' fwaa-appt--done' : ''}`}>
      <div className="fwaa-appt__head">
        <div className="fwaa-appt__who">
          <Link to={`/sales/fwaa/attendees/${appt.id}`} className="fwaa-appt__name">
            {appt.name}
          </Link>
          <RoleBadge roleType={appt.role_type} />
          <div className="fwaa-appt__meta">
            {appt.title ? `${appt.title} · ` : ''}{appt.company_name}
            {appt.property_count > 0 ? (
              <span className="fwaa-appt__stats">
                {' '}· {appt.property_count} {appt.property_count === 1 ? 'property' : 'properties'}
                {appt.apollo_count > 0 ? <span className="fwaa-attendee-card__apollo"> · {appt.apollo_count} Apollo</span> : null}
              </span>
            ) : null}
          </div>
          {(appt.email || appt.phone) ? (
            <div className="fwaa-contact fwaa-appt__contact">
              {appt.email ? <a href={`mailto:${appt.email}`}>{appt.email}</a> : null}
              {appt.email && appt.phone ? ' · ' : ''}
              {appt.phone ? <a href={`tel:${appt.phone}`}>{appt.phone}</a> : null}
            </div>
          ) : null}
        </div>
        <CompleteToggle attendeeId={appt.id} status={appt.status} onChanged={onChanged} />
      </div>
      <NotesThread attendeeId={appt.id} notes={appt.notes} myUserId={myUserId} onChanged={onChanged} />
    </div>
  );
}
